import { useEffect, useMemo, useState } from "react";
import { Building2, CheckCircle2, Clock, Inbox, Mail, RefreshCw, Search } from "lucide-react";
import { Layout } from "@/components/Layout";
import { DataPanel, EmptyState, MetricStrip, Surface } from "@/components/CommandSystem";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { fmtRelative } from "@/lib/format";

function parseContactMessage(message = "") {
  const parsed = { name: "", email: "", agency: "", message: "" };
  const [header = "", ...bodyParts] = String(message).split(/\n\nMessage:\n/);

  parsed.message = bodyParts.join("\n\nMessage:\n").trim();

  header.split(/\n/).forEach((line) => {
    const [rawKey = "", ...rest] = line.split(":");
    const key = rawKey.trim().toLowerCase();
    const value = rest.join(":").trim();

    if (key === "name") parsed.name = value;
    if (key === "email") parsed.email = value;
    if (key === "agency" || key === "company") parsed.agency = value;
  });

  return parsed;
}

function localDayKey(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "Unknown date";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dayLabel(key) {
  if (key === "Unknown date") return key;
  const date = new Date(`${key}T00:00:00`);
  return date.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function includesText(enquiry, query) {
  if (!query) return true;
  const haystack = [
    enquiry.parsed.name,
    enquiry.parsed.email,
    enquiry.parsed.agency,
    enquiry.parsed.message,
  ].join(" ").toLowerCase();
  return haystack.includes(query.toLowerCase());
}

export function Enquiries() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/notifications", { skipGlobalLoader: true });
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setError("Could not load enquiries right now.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const enquiries = useMemo(() => {
    return items
      .filter((item) => String(item.title || "").toLowerCase().includes("contact request"))
      .map((item) => ({ ...item, parsed: parseContactMessage(item.message) }))
      .filter((item) => includesText(item, query));
  }, [items, query]);

  const grouped = useMemo(() => {
    const groups = enquiries.reduce((acc, enquiry) => {
      const key = localDayKey(enquiry.created_at);
      if (!acc[key]) acc[key] = [];
      acc[key].push(enquiry);
      return acc;
    }, {});

    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, rows]) => ({
        key,
        label: dayLabel(key),
        rows: rows.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
      }));
  }, [enquiries]);

  const unread = enquiries.filter((item) => !item.read).length;
  const todayKey = localDayKey(new Date());
  const todayCount = enquiries.filter((item) => localDayKey(item.created_at) === todayKey).length;

  async function markRead(id) {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, read: true } : item)));
    await api.put(`/notifications/${id}/read`);
    window.dispatchEvent(new Event("hx:notifications-updated"));
  }

  return (
    <Layout
      title="Enquiries"
      subtitle="Home page form submissions, grouped by received day for super admin follow-up."
      actions={
        <Button size="sm" variant="outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </Button>
      }
    >
      <MetricStrip
        metrics={[
          { label: "Total enquiries", value: enquiries.length, hint: "Visible contact requests", tone: "blue", icon: Inbox },
          { label: "Unread", value: unread, hint: "Pending review", tone: unread ? "rose" : "emerald", icon: Mail },
          { label: "Today", value: todayCount, hint: dayLabel(todayKey), tone: "indigo", icon: Clock },
        ]}
      />

      <DataPanel
        title="Submitted responses"
        subtitle="Search by name, email, company, or message."
        actions={
          <div className="relative w-full min-w-[220px] sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search enquiries"
              className="pl-9"
            />
          </div>
        }
        className="max-w-6xl"
      >
        {error && (
          <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700">
            {error}
          </div>
        )}

        {loading ? (
          <Surface className="p-6 text-sm font-semibold text-slate-500">Loading enquiries...</Surface>
        ) : grouped.length === 0 ? (
          <Surface className="p-6">
            <EmptyState
              title={query ? "No matching enquiries" : "No enquiries yet"}
              description={query ? "Try a different search term." : "Home page submissions will appear here after users send the form."}
            />
          </Surface>
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => (
              <Surface key={group.key} className="overflow-hidden p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
                  <div>
                    <div className="text-sm font-bold text-slate-950">{group.label}</div>
                    <div className="text-xs font-medium text-slate-500">{group.rows.length} submitted response{group.rows.length === 1 ? "" : "s"}</div>
                  </div>
                </div>

                <div className="divide-y divide-slate-100">
                  {group.rows.map((enquiry) => (
                    <article key={enquiry.id} className="grid gap-4 px-4 py-4 lg:grid-cols-[240px_1fr_auto]">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${enquiry.read ? "bg-slate-300" : "bg-blue-600"}`} />
                          <h3 className="truncate text-sm font-bold text-slate-950">
                            {enquiry.parsed.name || "Unknown contact"}
                          </h3>
                        </div>
                        <a className="mt-2 flex items-center gap-2 truncate text-sm font-semibold text-blue-700" href={enquiry.parsed.email ? `mailto:${enquiry.parsed.email}` : undefined}>
                          <Mail size={14} />
                          {enquiry.parsed.email || "No email provided"}
                        </a>
                        <div className="mt-2 flex items-center gap-2 truncate text-xs font-semibold text-slate-500">
                          <Building2 size={14} />
                          {enquiry.parsed.agency || "No company provided"}
                        </div>
                      </div>

                      <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                        {enquiry.parsed.message || enquiry.message || "No message provided."}
                      </p>

                      <div className="flex items-start justify-between gap-3 lg:flex-col lg:items-end">
                        <div className="text-right text-xs font-semibold text-slate-500">
                          {fmtRelative(enquiry.created_at)}
                        </div>
                        {!enquiry.read ? (
                          <Button size="sm" variant="outline" onClick={() => markRead(enquiry.id)}>
                            <CheckCircle2 size={14} />
                            Mark read
                          </Button>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500">Read</span>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              </Surface>
            ))}
          </div>
        )}
      </DataPanel>
    </Layout>
  );
}
